const PADDLE_SCRIPT_SRC = "https://cdn.paddle.com/paddle/v2/paddle.js";

let paddleLoadPromise = null;

function getPaddleGlobals() {
  return typeof window !== "undefined" ? window.Paddle : undefined;
}

export function loadPaddleScript() {
  if (getPaddleGlobals()) {
    return Promise.resolve(getPaddleGlobals());
  }

  if (paddleLoadPromise) {
    return paddleLoadPromise;
  }

  paddleLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PADDLE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(getPaddleGlobals()));
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Paddle.js"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PADDLE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(getPaddleGlobals());
    script.onerror = () => reject(new Error("Failed to load Paddle.js"));
    document.head.appendChild(script);
  });

  return paddleLoadPromise;
}

export async function initializePaddle() {
  const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
  const environment = import.meta.env.VITE_PADDLE_ENVIRONMENT || "sandbox";

  if (!clientToken) {
    throw new Error("VITE_PADDLE_CLIENT_TOKEN is not configured");
  }

  const Paddle = await loadPaddleScript();
  if (!Paddle) {
    throw new Error("Paddle.js did not initialize");
  }

  if (environment === "sandbox") {
    Paddle.Environment.set("sandbox");
  }

  if (!window.__paddleInitialized) {
    Paddle.Initialize({
      token: clientToken,
      eventCallback: (event) => {
        if (event?.name === "checkout.completed") {
          window.location.assign(`${window.location.origin}/subscribe/success`);
        }
      },
    });
    window.__paddleInitialized = true;
  }

  return Paddle;
}

export async function openPaddleCheckout({
  priceId,
  userId,
  productName = "GrammarLocker",
  country = "",
  usePointsDiscount = false,
  discountId,
  paymentId,
  customerEmail,
}) {
  if (!priceId) {
    throw new Error("Paddle price ID is missing");
  }
  if (!userId) {
    throw new Error("user_id is required for Paddle checkout");
  }

  const Paddle = await initializePaddle();

  const checkoutOptions = {
    items: [{ priceId, quantity: 1 }],
    customData: {
      user_id: String(userId),
      product_name: String(productName || "GrammarLocker"),
      country: String(country || ""),
      use_points_discount: usePointsDiscount ? "true" : "false",
      payment_id: paymentId ? String(paymentId) : "",
    },
    settings: {
      successUrl: `${window.location.origin}/subscribe/success`,
      // XP discount is applied via discountId from our backend — no coupon code for the user.
      showAddDiscounts: false,
    },
  };

  if (discountId) {
    checkoutOptions.discountId = String(discountId);
  }

  if (customerEmail) {
    checkoutOptions.customer = { email: customerEmail };
  }

  Paddle.Checkout.open(checkoutOptions);
}
